// Shared team name utilities used across HomePage, TeamPage, and LeaguePage.
// Maps full DB / TheSportsDB team name (lowercase) → short logo filename stem.

export const TEAM_NAME_MAP = {
  // NBA
  'philadelphia 76ers':        '76ers',
  'portland trail blazers':    'Blazers',
  'milwaukee bucks':           'Bucks',
  'chicago bulls':             'Bulls',
  'cleveland cavaliers':       'Cavaliers',
  'boston celtics':            'Celtics',
  'los angeles clippers':      'Clippers',
  'memphis grizzlies':         'Grizzlies',
  'atlanta hawks':             'Hawks',
  'miami heat':                'Heat',
  'charlotte hornets':         'Hornets',
  'sacramento kings':          'Kings',
  'new york knicks':           'Knicks',
  'los angeles lakers':        'Lakers',
  'dallas mavericks':          'Mavericks',
  'brooklyn nets':             'Nets',
  'denver nuggets':            'Nuggets',
  'indiana pacers':            'Pacers',
  'new orleans pelicans':      'Pelicans',
  'detroit pistons':           'Pistons',
  'toronto raptors':           'Raptors',
  'houston rockets':           'Rockets',
  'san antonio spurs':         'Spurs',
  'phoenix suns':              'Suns',
  'oklahoma city thunder':     'Thunder',
  'minnesota timberwolves':    'Timberwolves',
  'golden state warriors':     'Warriors',
  'washington wizards':        'Wizards',
  // EPL
  'arsenal':                   'Arsenal',
  'aston villa':               'Aston Villa',
  'bournemouth':               'Bournemouth',
  'brentford':                 'Brentford',
  'brighton and hove albion':  'Brighton',
  'brighton':                  'Brighton',
  'burnley':                   'Burnley',
  'chelsea':                   'Chelsea',
  'crystal palace':            'Crystal Palace',
  'everton':                   'Everton',
  'fulham':                    'Fulham',
  'leeds united':              'Leeds',
  'liverpool':                 'Liverpool',
  'manchester city':           'Manchester City',
  'manchester united':         'Manchester United',
  'newcastle united':          'Newcastle',
  'newcastle':                 'Newcastle',
  'nottingham forest':         'Nottingham Forest',
  'tottenham hotspur':         'Tottenham',
  'tottenham':                 'Tottenham',
  'west ham united':           'West Ham',
  'west ham':                  'West Ham',
  'wolverhampton wanderers':   'Wolves',
  'wolves':                    'Wolves',
  'sunderland':                'Sunderland',
  // MLS
  'atlanta united':            'Atlanta',
  'austin fc':                 'Austin',
  'charlotte fc':              'Charlotte',
  'chicago fire':              'Chicago',
  'fc cincinnati':             'Cincinnati',
  'colorado rapids':           'Colorado',
  'columbus crew':             'Columbus',
  'fc dallas':                 'Dallas',
  'dc united':                 'DC United',
  'houston dynamo':            'Houston',
  'sporting kansas city':      'Kansas City',
  'la galaxy':                 'L.A. Galaxy',
  'l.a. galaxy':               'L.A. Galaxy',
  'los angeles galaxy':        'L.A. Galaxy',
  'los angeles fc':            'Los Angeles',
  'lafc':                      'Los Angeles',
  'inter miami':               'Miami',
  'inter miami cf':            'Miami',
  'minnesota united':          'Minnesota',
  'cf montréal':               'Montreal',
  'cf montreal':               'Montreal',
  'nashville sc':              'Nashville',
  'new england revolution':    'New England',
  'new york city fc':          'New York City',
  'new york red bulls':        'New York',
  'orlando city':              'Orlando',
  'philadelphia union':        'Philadelphia',
  'portland timbers':          'Portland',
  'real salt lake':            'Salt Lake',
  'san diego fc':              'San Diego',
  'san jose earthquakes':      'San Jose',
  'seattle sounders':          'Seattle',
  'seattle sounders fc':       'Seattle',
  'st. louis city sc':         'St. Louis',
  'toronto fc':                'Toronto',
  'vancouver whitecaps':       'Vancouver',
  'vancouver whitecaps fc':    'Vancouver',
  // MLB
  'los angeles angels':        'Angels',
  'houston astros':            'Astros',
  'athletics':                 'Athletics',
  'oakland athletics':         'Athletics',
  'toronto blue jays':         'Blue Jays',
  'atlanta braves':            'Braves',
  'milwaukee brewers':         'Brewers',
  'st. louis cardinals':       'Cardinals',
  'chicago cubs':              'Cubs',
  'arizona diamondbacks':      'Diamondbacks',
  'los angeles dodgers':       'Dodgers',
  'san francisco giants':      'Giants',
  'cleveland guardians':       'Guardians',
  'seattle mariners':          'Mariners',
  'miami marlins':             'Marlins',
  'new york mets':             'Mets',
  'washington nationals':      'Nationals',
  'baltimore orioles':         'Orioles',
  'san diego padres':          'Padres',
  'philadelphia phillies':     'Phillies',
  'pittsburgh pirates':        'Pirates',
  'texas rangers':             'Rangers',
  'tampa bay rays':            'Rays',
  'boston red sox':            'Red Sox',
  'cincinnati reds':           'Reds',
  'colorado rockies':          'Rockies',
  'kansas city royals':        'Royals',
  'detroit tigers':            'Tigers',
  'minnesota twins':           'Twins',
  'chicago white sox':         'White Sox',
  'new york yankees':          'Yankees',
};

/**
 * Given a full team name (any case), returns the short display name for logos.
 * Falls back to the last word of the name if no mapping found.
 */
export const getTeamDisplayName = (fullName) => {
  const lower = fullName.toLowerCase().trim();
  if (TEAM_NAME_MAP[lower]) return TEAM_NAME_MAP[lower];
  const words = fullName.trim().split(/\s+/);
  const last = words[words.length - 1];
  return last.charAt(0).toUpperCase() + last.slice(1).toLowerCase();
};

/**
 * Returns the local /Logos/ path for a team given its full name (any case).
 * The filename convention is: first character uppercase, rest lowercase.
 * e.g. "L.A. Galaxy" → "/Logos/L.a. galaxy.png"
 *      "Aston Villa"  → "/Logos/Aston villa.png"
 */
export const getLogoPath = (fullName) => {
  const shortName = getTeamDisplayName(fullName);
  return `/Logos/${shortName.charAt(0).toUpperCase() + shortName.slice(1).toLowerCase()}.png`;
};

/**
 * Parse a game date (dd/mm/yyyy) and time (h:mmam/pm) into a Date object.
 * Returns null if parsing fails.
 */
export const parseGameStart = (date, time) => {
  if (!date || !time) return null;
  try {
    const [d, m, y] = date.split('/').map(Number);
    const match = (time || '').trim().match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
    if (!match) return null;
    let hour = Number(match[1]);
    const min  = Number(match[2]);
    const ampm = match[3].toLowerCase();
    if (ampm === 'pm' && hour !== 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;
    return new Date(Date.UTC(y, m - 1, d, hour, min));
  } catch {
    return null;
  }
};

/**
 * Returns true if a game is locked for picking:
 * the game has already started, is ongoing, or has finished.
 * game must have: { date, time, winning_team }
 */
export const isGameLocked = (game) => {
  if (!game) return true;
  if (game.winning_team) return true;
  const start = parseGameStart(game.date, game.time);
  if (!start) return false;
  return new Date() >= start;
};
